import React, { ReactNode } from 'react'

import styled from '@emotion/styled'

import { Box, RemoveButton, ProgressBar, Text, FlexBox } from '@surglogs-app/ui'

import { useTranslation } from '../services'

import { File } from './File'

const StyledProgressBar = styled(ProgressBar)`
  height: ${({ theme }) => theme.ui.spacings.xs};
`

const Divider = styled(Box)`
  width: 100%;
  max-width: ${({ theme }) => `calc(100% - ${theme.ui.spacings.xxxl})`};
  margin-left: auto;
`

interface Props {
  children?: ReactNode
  prependChildren?: ReactNode
  appendChildren?: ReactNode
  fileName: string
  uploadProgress?: number
  thumbnail?: string
  url?: string | null
  onRemoveClick?(): void
  canRemoveFile?: boolean
  withDivider?: boolean
  isInvalid?: boolean
}

export const UploadedFileItem = ({
  children,
  prependChildren,
  appendChildren,
  fileName,
  url,
  thumbnail,
  onRemoveClick,
  uploadProgress,
  canRemoveFile = true,
  withDivider = true,
  isInvalid = false,
  ...rest
}: Props) => {
  const { t } = useTranslation()
  const isUploading = !!fileName && !url

  if (isUploading) {
    return (
      <Box
        {...{
          px: 'lg',
          py: 'md',
          bra: 'sm',
          bgc: 'white.100',
          bd: '1px solid',
          bc: 'navy.25',
          w: '100%',
        }}>
        <Text {...{ size: 'xxs' }}>{t('uploading')}</Text>
        <Text {...{ display: 'block', mb: 'sm' }}>{fileName}</Text>
        <StyledProgressBar
          {...{
            percentage: uploadProgress || 0,
            'aria-label': 'file-progressbar',
          }}
        />
      </Box>
    )
  }

  return (
    <Box
      {...{
        w: '100%',
        role: 'group',
        'aria-label': 'uploaded-file',
        'aria-invalid': isInvalid,
        ...rest,
      }}>
      <FlexBox {...{ gap: 'md' }}>
        {prependChildren}
        <File
          {...{
            fileName: fileName,
            thumbnailUrl: thumbnail,
            url: isUploading ? null : String(url),
            role: 'group',
            'aria-label': 'file-thumbnail',
            isInvalid,
          }}
        />
        {children}
        <RemoveButton
          {...{
            shape: 'square',
            onClick: () => onRemoveClick?.(),
            'aria-label': 'delete-file',
            disabled: !canRemoveFile,
          }}
        />
        {appendChildren}
      </FlexBox>
      {withDivider ? (
        <Divider {...{ h: 'xxxs', bgc: 'navy.25', mt: 'md' }} />
      ) : null}
    </Box>
  )
}
